import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../features/auth/auth-context';
import { DemoAccounts } from '../features/auth/components/demo-accounts';
import { GoogleSignIn } from '../features/auth/components/google-sign-in';
import { joinTokenOf, orgInviteApi, orgInvitePreviewKey } from '../features/organizations/api';
import { OrgInviteCard } from '../features/organizations/components/org-invite-card';
import { safeNext } from '../lib/safe-next';

/**
 * `?next=` — đường quay lại sau khi đăng nhập, dùng cho link mời (APM-1317b).
 *
 * `z.string()` là đúng dù router phân tích search param bằng `JSON.parse`:
 * `/invite/abc` không phải JSON hợp lệ nên nó tới schema nguyên dạng chuỗi.
 * Lọc chống open redirect nằm ở `safeNext` — xem lý do ở đó.
 */
export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: z.object({
    next: z.string().optional().catch(undefined).transform(safeNext),
    /**
     * Lý do một lượt đăng nhập Google hỏng — APM-AUTH-g, sửa nghĩa ở APM-GAUTH-c.
     *
     * `denied` **từng** gộp hai chuyện: "email chưa có tài khoản" và "Google từ
     * chối". Nhánh đầu không còn tồn tại — email lạ nay đi thẳng sang
     * `/onboarding` và có tài khoản luôn. Nên `denied` giờ chỉ còn nghĩa: Google
     * từ chối, userinfo hỏng, email chưa xác minh, hoặc **tài khoản đã bị vô
     * hiệu hoá**.
     *
     * Cửa chống dò email không mất đi mà **hết cần** ở đây: mọi email xác minh
     * được đều đi tiếp, nên không còn tín hiệu nào để dò. (`POST /auth/register`
     * vốn đã trả `EMAIL_ALREADY_REGISTERED`, nên cánh cửa ấy chưa bao giờ đóng.)
     * Thứ vẫn phải giấu là **trạng thái** của một tài khoản có thật, và câu chữ
     * ở `auth.googleError.denied` nói chung chung đúng vì lẽ đó.
     */
    googleError: z.enum(['state', 'denied', 'suspended']).optional().catch(undefined),
  }),
});

function LoginPage(): ReactNode {
  const { t } = useTranslation();
  const { status, expired } = useAuth();
  const navigate = useNavigate();
  const { next, googleError } = Route.useSearch();
  /** `href` chứ không `to`: `next` là chuỗi lúc chạy, không phải route đã biết. */
  const goOn = useCallback(
    () => void navigate({ href: next ?? '/', replace: true }),
    [navigate, next],
  );

  // Đã đăng nhập mà mở /login (vd bấm nút Back) thì đi tiếp luôn.
  useEffect(() => {
    if (status === 'authenticated') goOn();
  }, [status, goOn]);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-muted p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.signInTitle')}</CardTitle>
          <CardDescription>{t('auth.signInSubtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <JoinInvite next={next} />
          {expired && (
            <p role="status" className="rounded-DEFAULT bg-rag-amber/10 p-3 text-sm">
              {t('auth.sessionExpired')}
            </p>
          )}
          {googleError && (
            <p role="status" className="rounded-DEFAULT bg-rag-red/10 p-3 text-sm">
              {t(`auth.googleError.${googleError}`)}
            </p>
          )}
          {/*
            Một đường vào duy nhất: Google — APM-ORG-h1.

            Ô email + mật khẩu và link "Tạo tổ chức mới" đã bỏ khỏi đây. Cả hai
            từng đứng cạnh nhau và cùng trả lời sai một câu hỏi: người mới tới
            đọc "Chưa có tài khoản? Tạo tổ chức mới" rồi dựng ra một tổ chức
            thứ hai rỗng không, trong khi thứ họ cần là được mời vào tổ chức đã
            có. Nay tổ chức chỉ tạo được **sau khi đăng nhập**, ở Cài đặt của
            tôi — chỗ người dùng đã thấy mình đang ở đâu trước khi mở thêm chỗ mới.

            ⚠️ Hệ quả phải biết: `/auth/login` và `/auth/register` vẫn còn ở
            API (e2e và script vận hành dùng), nhưng **không còn màn hình nào**
            gọi chúng. Một bản cài chưa cấu hình Google thì chỉ vào được bằng
            tài khoản demo — nên `GoogleSignIn` nay nói thẳng khi chưa cấu hình
            thay vì ẩn đi.
          */}
          <GoogleSignIn next={next} />
          {/*
            Đến từ link sổ chốt (`next=/d/…`) thì không mời vào bằng tài khoản
            demo: tài khoản demo không trả lời sổ được (chốt 2026-10-07), nên
            chọn nó chỉ đưa người ta quay lại đúng chỗ vừa rời, vẫn là khách.
          */}
          {next?.startsWith('/d/') ? null : <DemoAccounts onSuccess={goOn} />}
        </CardContent>
      </Card>
    </main>
  );
}

/**
 * Thẻ lời mời ở trên form đăng nhập — APM-ORGP-b.
 *
 * `/join/$token` nằm dưới `_app`, nên người chưa đăng nhập bấm link mời bị đá
 * thẳng sang đây. Trước đây họ thấy một form đăng nhập trần và không biết mình
 * được mời vào đâu, bởi ai — đúng lúc họ phải quyết định có tin cái link ấy
 * không.
 *
 * Token hỏng, hết hạn, hay đang tải thì **không hiện gì**: đăng nhập vẫn là
 * việc chính của trang, và `/join` sẽ nói rõ "link không còn hiệu lực" sau khi
 * họ vào. Một khung lỗi ở đây chỉ chắn trước nút đăng nhập.
 */
function JoinInvite({ next }: { readonly next: string | undefined }): ReactNode {
  const token = joinTokenOf(next);
  const preview = useQuery({
    queryKey: orgInvitePreviewKey(token ?? ''),
    queryFn: () => orgInviteApi.preview(token!),
    enabled: token !== null,
    retry: false,
  });

  if (token === null || !preview.data) return null;
  return <OrgInviteCard preview={preview.data} />;
}
